import { useAuth } from "@/context/AuthContext";
import { Eye, X } from "lucide-react";

/**
 * Persistent banner shown at the top of the viewport whenever an admin
 * has toggled "Preview as client" mode. Serves two purposes:
 *   1. Constant visual reminder that what they're seeing is the CLIENT
 *      view, so they don't get confused when the sidebar disappears.
 *   2. One-click exit — no need to hunt down the toggle in Settings.
 *
 * Rendered at the top of the app tree (inside AuthProvider but outside
 * the routes) so it appears above both ClientLayout and the auth-page
 * chrome. Hidden entirely for users who aren't currently previewing.
 */
export default function PreviewAsClientBanner() {
  const { previewAsClient, togglePreviewAsClient, user } = useAuth();
  if (!previewAsClient) return null;
  return (
    <div
      role="status"
      className="sticky top-0 z-50 w-full bg-amber-500 text-black px-4 py-2 flex items-center gap-3 shadow-lg print:hidden"
      data-testid="preview-as-client-banner"
    >
      <Eye className="h-4 w-4 shrink-0" />
      <div className="text-xs sm:text-sm font-semibold tracking-wide flex-1 truncate">
        Preview mode — seeing the app as a <span className="uppercase">client</span>.
        {user?._real_role && (
          <span className="hidden sm:inline text-black/70 font-normal">
            {" "}Your real role is <span className="font-semibold">{user._real_role}</span> and will resume when you exit.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => togglePreviewAsClient(false)}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/85 hover:bg-black text-amber-700 text-xs font-semibold uppercase tracking-wider"
        data-testid="preview-as-client-exit"
      >
        <X className="h-3.5 w-3.5" />
        Exit preview
      </button>
    </div>
  );
}
